/**
 * Form View Render Component
 * 
 * Dynamic HTML generator component that programmatically builds form display interfaces
 * based on form metadata, field metadata, and form step definitions. This component
 * serves as the core rendering engine for both form viewing and PDF generation,
 * ensuring consistency between UI display and backend PDF output.
 * 
 * Key Functionality:
 * - Dynamic form structure generation from metadata and step definitions
 * - Programmatic HTML building based on form field types and configurations
 * - Support for complex form layouts including steps, sections, and checkbox groups
 * - Attachment and signature handling with visual representation
 * - Responsive design with alternating background colors for readability
 * 
 * Architecture:
 * - Processes FormStep[] arrays to build hierarchical form structure
 * - Uses FormFieldMetadata to determine field types and display behavior
 * - Generates ProcessedStep, ProcessedSection, and ProcessedField objects
 * - Handles both structured (stepped) and unstructured (orphaned) field layouts
 * 
 * Form Structure Processing:
 * - Steps: Top-level form divisions (e.g., "Basic Info", "Fall Hazards")
 * - Sections: Content areas within steps containing fields and checkbox groups
 * - Fields: Individual form inputs with type-specific rendering
 * - Checkbox Groups: Related checkbox fields grouped under common labels
 * 
 * Field Type Support:
 * - Regular fields: Text, dates, numbers, etc.
 * - Upload arrays: File attachments with visual representation
 * - Signature arrays: Digital signatures with preview capabilities
 * - Checkbox fields: Boolean values grouped by logical categories
 * 
 * Data Processing Pipeline:
 * 1. Form steps are processed and enhanced with submission details
 * 2. Each step's sections are processed to extract fields and checkbox groups
 * 3. Field metadata is applied to determine display behavior
 * 4. Empty/blank fields are filtered out for cleaner display
 * 5. Processed data is cached for performance optimization
 * 
 * Usage Scenarios:
 * - Form View Component: Displays submitted forms for review
 * - Form Render Service: Generates HTML for PDF creation
 * - PDF Generation: Ensures consistent output between UI and PDF
 * - Form Preview: Shows form structure before submission
 * 
 * Service Dependencies:
 * - FormModelService: Provides form metadata and field information
 * - File Type Utils: Determines file type display and icons
 * - Field Type Extractor: Provides field metadata for rendering decisions
 * 
 * @author MacSafety Development Team
 * @version 1.0
 */

import { Component, Input, Output, EventEmitter, OnInit, OnChanges } from '@angular/core';
import { FormStep, CheckboxGroup, InfoBlock,Section } from '../../../../models/forms/form-steps.interface';
import { ResolvedAttachments } from '../../../../models/forms/attachments/resolved-attachments';

import { ModalAttachments } from '../../../../models/modal-viewer-attachments';
import { getUserFriendlyFileType, getFileTypeDisplayName, getFileTypeIonicIcon } from '../../../../utils/file-type';
import { FormFieldMetadata } from '../../../../utils/field-type-extractor';
import { FormModelService } from '../../../../services/form-model.service';
import { WordPressField } from '../../../../models/forms/form-retrieval-response';

// ========================================
// PROCESSED DATA INTERFACES
// ========================================

/**
 * Interface for processed field data with metadata and display information
 * Contains all necessary information for rendering individual form fields
 */
interface ProcessedField {
  key: string;                    // Field identifier/key
  label: string;                  // Human-readable field label
  value: any;                     // Field value from form entry
  isDate: boolean;                // Whether field contains date data
  isUploadArray: boolean;         // Whether field contains file uploads
  isSignatureArray: boolean;      // Whether field contains signatures
  isString: boolean;              // Whether field contains string data
  isAttachment: boolean;          // Whether field contains any attachments
}

/**
 * Interface for processed section data containing fields and checkbox groups
 * Represents a logical grouping of form fields within a step
 */
interface ProcessedSection {
  title?: string;                 // Section title for display
  subtitle?: string;              // Section subtitle for additional context
    infoBlocks?: InfoBlock[];      // Optional informational blocks (paragraphs, lists) for the section
  fields: ProcessedField[];       // Regular form fields in this section
  checkboxGroups: ProcessedCheckboxGroup[]; // Checkbox field groups
  hasValues: boolean;             // Whether section contains any non-empty values
}

/**
 * Interface for processed checkbox group data
 * Represents a logical grouping of related checkbox fields
 */
interface ProcessedCheckboxGroup {
  title: string;                  // Group label/title
  checkedFields: ProcessedField[]; // Only the checked checkbox fields
  hasCheckedFields: boolean;      // Whether any checkboxes are checked
}

/**
 * Interface for processed step data with sections and metadata
 * Represents a complete form step with all its content and state
 */
interface ProcessedStep {
  id: string;                     // Step identifier
  title: string;                  // Step title for display
  icon: string;                   // Ionic icon for step representation
  sections: ProcessedSection[];   // Sections within this step
  hasValues: boolean;             // Whether step contains any non-empty values
}

@Component({
  selector: 'app-form-render',
  templateUrl: './form-view-render.component.html',
  styleUrls: ['./form-view-render.component.css']
})
export class FormRenderComponent implements OnInit, OnChanges {
  // ========================================
  // INPUT PROPERTIES
  // ========================================
  
  // Form data and configuration inputs
  @Input() formEntry: any = null;                    // The actual form data to render
  @Input() formSteps: FormStep[] = [];               // Form structure definition
  @Input() fieldLabels: Record<string, string> = {}; // Human-readable field labels
  @Input() formTitle: string = '';                   // Form title for display
  @Input() attachments?: ResolvedAttachments;        // File attachments and signatures
  @Input() fieldMetadata?: FormFieldMetadata;        // Field type metadata
  @Input() wordPressFields: WordPressField[] = [];   // WordPress-specific field data
  
  // ========================================
  // INTERNAL PROPERTIES
  // ========================================
  
  // Field metadata for rendering decisions
  formFieldMetadata: FormFieldMetadata = {};
  
  // Cached processed data for performance optimization
  processedStepsData: ProcessedStep[] = [];
  processedOrphanedFields: ProcessedField[] = [];
  
  // Event emitter for attachment interactions
  @Output() attachmentClick = new EventEmitter<{
    fieldKey: string,
    attachments: ModalAttachments[],
    selectedIndex: number
  }>();

  // Internal property to hold the processed steps with submission step added
  private _processedSteps: FormStep[] = [];

  constructor(private formModelService: FormModelService) {}

  // ========================================
  // LIFECYCLE METHODS
  // ========================================

  /**
   * Component initialization - processes form data and builds display structure
   * Called once when component is created
   */
  ngOnInit(): void {
    this.processFormSteps();
    this.setFormFieldMetadata();
    this.buildProcessedData();
  }

  /**
   * Handles input property changes - reprocesses data when inputs change
   * Called whenever any @Input property changes
   */
  ngOnChanges(): void {
    this.processFormSteps();
    this.setFormFieldMetadata();
    this.buildProcessedData();
  }

  // ========================================
  // METADATA MANAGEMENT
  // ========================================

  /**
   * Sets form field metadata from input or service fallback
   * Prioritizes passed-in metadata, falls back to service-based metadata
   */
  private setFormFieldMetadata(): void {
    // Use passed-in metadata first, fallback to service
    if (this.fieldMetadata) {
      this.formFieldMetadata = this.fieldMetadata;
      return;
    }

    // Fallback to service (for regular component usage)
    const formType = this.getFormTypeFromEntry();
    if (formType) {
      this.formFieldMetadata = this.formModelService.getFormMetadata(formType)?.fieldMetadata || {};
    } else {
      this.formFieldMetadata = {};
      console.warn('No form type detected and no field metadata provided');
    }
  }

  // ========================================
  // DATA PROCESSING PIPELINE
  // ========================================

  /**
   * Builds all processed data once and caches it for performance
   * Creates the complete data structure needed for template rendering
   */
  private buildProcessedData(): void {
    if (!this.formEntry) {
      this.processedStepsData = [];
      this.processedOrphanedFields = [];
      return;
    }

    // Build processed steps from form structure
    this.processedStepsData = this._processedSteps.map(step => this.buildProcessedStep(step));
    
    // Build processed orphaned fields (fields not in any step)
    this.processedOrphanedFields = this.buildProcessedFields(this.orphanedFieldKeys);
  }

  /**
   * Builds a processed step with all its sections and fields
   * Processes the complete step structure including all nested content
   * 
   * @param step - The form step to process
   * @returns ProcessedStep with all metadata and content
   */
  private buildProcessedStep(step: FormStep): ProcessedStep {
    const processedSections: ProcessedSection[] = [];

    // Process all sections within this step
    if (step.sections && step.sections.length > 0) {
      step.sections.forEach(section => {
        processedSections.push(this.buildProcessedSection(section));
      });
    }

    // Check if step has any values for conditional display
    const hasValues = processedSections.some(section => section.hasValues);

    return {
      id: step.id,
      title: step.title,
      icon: step.icon,
      sections: processedSections,
      hasValues
    };
  }

  /**
   * Builds a processed section with its fields and checkbox groups
   * Separates regular fields from checkbox fields and processes each type
   * 
   * @param section - The section to process
   * @returns ProcessedSection with all fields and metadata
   */
  private buildProcessedSection(section: Section): ProcessedSection {
    // Get checkbox field keys to exclude from regular fields
    const checkboxFieldKeys = section.checkboxGroups?.flatMap(group => [group.checkboxLabel, ...group.fields]) || [];
    const regularFieldKeys = section.fields?.filter(field => !checkboxFieldKeys.includes(field)) || [];
    
    // Build processed fields and checkbox groups
    const processedFields = this.buildProcessedFields(regularFieldKeys);
    const processedCheckboxGroups = section.checkboxGroups?.map(group => this.buildProcessedCheckboxGroup(group)) || [];
    
    // Determine if section has any values for conditional display
    const hasFieldValues = processedFields.length > 0;
    const hasCheckboxValues = processedCheckboxGroups.some(group => group.hasCheckedFields);
      const hasInfoBlocks = (section.infoBlocks?.length ?? 0) > 0;
    const hasValues = hasFieldValues || hasCheckboxValues || hasInfoBlocks;
    console.log('[FormRenderComponent] Processed section has values:', { sectionTitle: section.title, hasFieldValues, hasCheckboxValues, hasInfoBlocks });

    return {
      title: section.title,
      subtitle: section.subtitle,
       infoBlocks: section.infoBlocks,  
      fields: processedFields,
      checkboxGroups: processedCheckboxGroups,
      hasValues
    };
  }

  /**
   * Builds processed fields array, filtering out empty values
   * Creates ProcessedField objects with metadata and display information
   * 
   * @param fieldKeys - Array of field keys to process
   * @returns Array of ProcessedField objects with non-empty values
   */
  private buildProcessedFields(fieldKeys: string[]): ProcessedField[] {
    return fieldKeys
      .map(key => this.buildProcessedField(key))
      .filter(field => !this.isFieldValueEmpty(field.value));
  }

  // ========================================
  // FIELD VALUE VALIDATION
  // ========================================

  /**
   * Checks if a field value is considered "empty" or "blank"
   * Handles various data types including strings, arrays, objects, and dates
   * 
   * @param value - The field value to check
   * @returns True if value is empty, false otherwise
   */
  private isFieldValueEmpty(value: any): boolean {
    if (value === null || value === undefined) {
      return true;
    }
    
    if (typeof value === 'string') {
      return value.trim() === '';
    }
    
    if (Array.isArray(value)) {
      return value.length === 0 || value.every(item => this.isFieldValueEmpty(item));
    }
    
    if (typeof value === 'object') {
      // Check if it's a Date object
      if (value instanceof Date) {
        return false; // Date objects are not empty (you could also check isNaN(value.getTime()) for invalid dates)
      }
      
      return Object.keys(value).length === 0;
    }
    
    return false;
  }

  // ========================================
  // INDIVIDUAL FIELD PROCESSING
  // ========================================

  /**
   * Builds a single processed field with all its metadata
   * Applies field type metadata and determines display behavior
   * 
   * @param key - The field key to process
   * @returns ProcessedField with complete metadata and display information
   */
  private buildProcessedField(key: string): ProcessedField {
    const metadata = this.formFieldMetadata[key] || {};
    const value = this.formEntry[key];
    
    // Prioritize upload array over signature array if both are true
    // This handles cases where metadata incorrectly marks a field as both
    const isUploadArray = metadata.isUploadArray || false;
    const isSignatureArray = !isUploadArray && (metadata.isSignatureArray || false);

    return {
      key,
      label: this.fieldLabels[key] || key,
      value,
      isDate: metadata.isDate || false,
      isUploadArray,
      isSignatureArray,
      isString: metadata.isString || false,
      isAttachment: isUploadArray || isSignatureArray,
    };
  }

  /**
   * Builds a processed checkbox group
   * Filters to only include checked checkboxes for display
   * 
   * @param group - The checkbox group to process
   * @returns ProcessedCheckboxGroup with checked fields only
   */
  private buildProcessedCheckboxGroup(group: CheckboxGroup): ProcessedCheckboxGroup {
    const checkedFieldKeys = group.fields.filter(field => this.formEntry[field] === true);
    const checkedFields = this.buildProcessedFields(checkedFieldKeys);
    
    return {
      title: group.checkboxLabel,
      checkedFields,
      hasCheckedFields: checkedFields.length > 0
    };
  }

  // ========================================
  // ORPHANED FIELD HANDLING
  // ========================================

  /**
   * Gets orphaned field keys (fields not belonging to any step)
   * These fields are displayed separately from the structured form layout
   */
  private get orphanedFieldKeys(): string[] {
    if (!this.formEntry || this._processedSteps.length === 0) {
      return this.entryKeys;
    }
    
    // Collect all fields from all sections in all steps
    const allStepFields: string[] = [];
    this._processedSteps.forEach(step => {
      if (step.sections && step.sections.length > 0) {
        step.sections.forEach(section => {
          allStepFields.push(...section.fields);
          if (section.checkboxGroups) {
            section.checkboxGroups.forEach(group => {
              allStepFields.push(group.checkboxLabel, ...group.fields);
            });
          }
        });
      }
    });
    
    return this.entryKeys.filter(key => !allStepFields.includes(key));
  }

  /**
   * Gets all entry keys excluding ID fields
   * Provides the complete list of form fields for processing
   */
  private get entryKeys(): Array<string> {
    if (!this.formEntry) return [];
    
    const idFields = ['id', 'Id', 'ID', '_id'];
    return Object.keys(this.formEntry).filter(key => !idFields.includes(key));
  }

  // ========================================
  // FORM STEP PROCESSING
  // ========================================

  /**
   * Processes the form steps and adds submission step if needed
   * Enhances the form structure with submission metadata when available
   */
  private processFormSteps(): void {
    if (!this.formEntry) {
      this._processedSteps = [...this.formSteps];
      return;
    }

    this._processedSteps = [...this.formSteps];
    this.addSubmissionStep();
  }

  /**
   * Adds submission details as the first step if submission fields exist
   * Creates a submission step with basic form metadata for display
   */
  private addSubmissionStep(): void {
    if (!this.formEntry) return;
    
    const submissionFields = ['submittedByName', 'submittedByEmail', 'submissionDate'];
    const existingSubmissionFields = submissionFields.filter(field => 
      this.formEntry.hasOwnProperty(field) && this.formEntry[field] !== null && this.formEntry[field] !== undefined
    );
    
    if (existingSubmissionFields.length > 0) {
      const submissionStep: FormStep = {
        id: 'submission-details',
        shortTitle: 'Submission',
        title: 'Entry Submission Details',
        icon: 'document-text-outline',
        description: 'Basic submission information',
        sections: [{
          fields: existingSubmissionFields,
        }]
      };
      
      this._processedSteps.unshift(submissionStep);
    }
  }

  // ========================================
  // PUBLIC GETTERS AND UTILITIES
  // ========================================

  /**
   * Checks if there are form steps defined for structured display
   * Determines whether to use stepped or flat field layout
   */
  get hasFormSteps(): boolean {
    return this._processedSteps.length > 0;
  }

  /**
   * Gets the form type from the formEntry object
   * Attempts multiple methods to determine form type for metadata lookup
   */
  private getFormTypeFromEntry(): string | null {
    if (!this.formEntry) return null;

    if (typeof this.formEntry.constructor.getFormInfo === 'function') {
      const formInfo = this.formEntry.constructor.getFormInfo();
      return formInfo.formType;
    }
    
    if (this.formEntry.hasOwnProperty('formType')) {
      return this.formEntry.formType;
    }
    
    return null;
  }

  // ========================================
  // ATTACHMENT HANDLING
  // ========================================

  /**
   * Gets attachment data for a field
   * Handles both upload attachments and signatures with proper metadata
   * 
   * @param fieldKey - The field key to get attachments for
   * @returns Array of attachment data with URLs, filenames, and metadata
   */
  getAttachmentData(fieldKey: string): Array<{url: string, filename: string, fileType?: string, size?: number}> {
    const fieldValue = this.formEntry[fieldKey];
    const metadata = this.formFieldMetadata[fieldKey];
    
    // Prioritize upload array over signature array
    const isUploadArray = metadata?.isUploadArray || false;
    const isSignatureArray = !isUploadArray && (metadata?.isSignatureArray || false);
    
    if (!isUploadArray && !isSignatureArray) {
      return [];
    }

    // If no attachments provided, return placeholder data
    if (!this.attachments) {
      const filenames = this.getAttachmentFilenames(fieldKey);
      const attachmentFieldType = isUploadArray ? 'Attachment' : 'Signature';
      
      return filenames.map(filename => ({
        url: `ATTACHMENT_PLACEHOLDER_${attachmentFieldType.toUpperCase()}_${fieldKey}_${filename}`,
        filename: filename,
      }));
    }

    // Check if it's an upload attachment
    if (isUploadArray) {
      const uploads = this.attachments.uploads[fieldKey];
      if (uploads) {
        return uploads
          .filter(attachment => attachment.isResolved)
          .map((attachment, index) => {
            const modalAttachment = new ModalAttachments(attachment);
            return {
              url: modalAttachment.url,
              filename: attachment.fileName || fieldValue[index] || this.extractFilename(attachment.blobName),
              fileType: modalAttachment.type,
              size: modalAttachment.size
            };
          })
          .filter(data => data.url && data.filename);
      }
    }

    // Check if it's a signature attachment
    if (isSignatureArray) {
      const signatures = this.attachments.signatures[fieldKey];
      if (signatures) {
        return signatures
          .filter(attachment => attachment.isResolved)
          .map((attachment, index) => {
            const modalAttachment = new ModalAttachments(attachment);
            return {
              url: modalAttachment.url,
              filename: fieldValue[index] || this.extractFilename(attachment.blobName),
              fileType: modalAttachment.type,
              size: modalAttachment.size
            };
          })
          .filter(data => data.url && data.filename);
      }
    }

    return [];
  }

  /**
   * Gets attachment filenames for a field
   * Extracts filenames from field values for display purposes
   * 
   * @param fieldKey - The field key to get filenames for
   * @returns Array of filenames for the field
   */
  getAttachmentFilenames(fieldKey: string): string[] {
    const fieldValue = this.formEntry[fieldKey];
    const metadata = this.formFieldMetadata[fieldKey];
    
    // Prioritize upload array over signature array
    const isUploadArray = metadata?.isUploadArray || false;
    const isSignatureArray = !isUploadArray && (metadata?.isSignatureArray || false);
    
    if (!isUploadArray && !isSignatureArray) {
      return [];
    }

    if (Array.isArray(fieldValue)) {
      return fieldValue;
    }

    return [];
  }

  /**
   * Gets the attachment type for display purposes
   * Determines whether field contains uploads or signatures
   * 
   * @param fieldKey - The field key to check
   * @returns String indicating attachment type ("Attachment" or "Signature")
   */
  getAttachmentFieldType(fieldKey: string): string {
    const metadata = this.formFieldMetadata[fieldKey];
    
    // Prioritize upload array over signature array
    if (metadata?.isUploadArray) {
      return 'Attachment';
    }
    
    if (metadata?.isSignatureArray) {
      return 'Signature';
    }
    
    return '';
  }

  /**
   * Determines if this is a placeholder attachment (no real URL)
   * Used to identify when attachments are not yet resolved
   * 
   * @param url - The attachment URL to check
   * @returns True if URL is a placeholder, false otherwise
   */
  isPlaceholderAttachment(url: string): boolean {
    return url.startsWith('ATTACHMENT_PLACEHOLDER_');
  }

  // ========================================
  // ATTACHMENT INTERACTIONS
  // ========================================

  /**
   * Handles attachment click events
   * Emits attachment click events for modal display or other interactions
   * 
   * @param url - The attachment URL that was clicked
   * @param filename - The filename of the clicked attachment
   * @param fieldKey - The field key containing the attachment
   */
  onAttachmentClick(url: string, filename: string, fieldKey: string): void {
    const fieldAttachments = this.getAttachmentData(fieldKey);
    const modalAttachments: ModalAttachments[] = fieldAttachments.map((attachment, index) => {
      return new ModalAttachments({
        name: attachment.filename,
        size: attachment.size || 0,
        type: attachment.fileType || '',
        url: attachment.url
      });
    });

    const selectedIndex = fieldAttachments.findIndex(att => att.url === url);

    this.attachmentClick.emit({
      fieldKey,
      attachments: modalAttachments,
      selectedIndex: Math.max(0, selectedIndex)
    });
  }

  // ========================================
  // VISUAL LAYOUT UTILITIES
  // ========================================

  /**
   * Gets the visual row index for a field, accounting for responsive behavior
   * Used for alternating background colors in responsive grid layouts
   * 
   * @param fieldIndex - The field index in the section
   * @returns Visual row index for styling purposes
   */
  getVisualRowIndex(fieldIndex: number): number {
    return Math.floor(fieldIndex / 2);
  }

  /**
   * Checks if a field should have even background color
   * Used for alternating row styling in responsive layouts
   * 
   * @param fieldIndex - The field index in the section
   * @returns True if field should have even background
   */
  shouldHaveEvenBackground(fieldIndex: number): boolean {
    const visualRowIndex = this.getVisualRowIndex(fieldIndex);
    return visualRowIndex % 2 === 0;
  }

  /**
   * Checks if a field should have odd background color
   * Used for alternating row styling in responsive layouts
   * 
   * @param fieldIndex - The field index in the section
   * @returns True if field should have odd background
   */
  shouldHaveOddBackground(fieldIndex: number): boolean {
    const visualRowIndex = this.getVisualRowIndex(fieldIndex);
    return visualRowIndex % 2 === 1;
  }

  // ========================================
  // FILE TYPE UTILITIES
  // ========================================

  /**
   * Gets file display name using file type utilities
   * Delegates to file-type utility functions for consistent display
   */
  getFileDisplayName(contentType: string, fileName?: string): string {
    return getFileTypeDisplayName(contentType, fileName);
  }
  
  /**
   * Gets file icon using file type utilities
   * Delegates to file-type utility functions for consistent icons
   */
  getFileIcon(contentType: string, fileName?: string): string {
    return getFileTypeIonicIcon(contentType, fileName);
  }
  
  /**
   * Gets complete file info using file type utilities
   * Delegates to file-type utility functions for comprehensive file data
   */
  getFileInfo(contentType: string, fileName?: string) {
    return getUserFriendlyFileType(contentType, fileName);
  }

  // ========================================
  // ATTACHMENT TYPE DETECTION
  // ========================================

  /**
   * Checks if an attachment is an image based on its MIME type
   * Used to determine appropriate display method (preview vs download)
   * 
   * @param attachment - The attachment to check
   * @returns True if attachment is an image, false otherwise
   */
  isImageAttachment(attachment: {url: string, filename: string, fileType: string}): boolean {
    return attachment.fileType.startsWith('image/');
  }

  /**
   * Handles non-image attachment clicks (opens in new tab)
   * Provides download functionality for non-image file types
   * 
   * @param url - The attachment URL to open
   * @param filename - The filename for reference
   */
  onFileAttachmentClick(url: string, filename: string): void {
    if (!this.isPlaceholderAttachment(url)) {
      window.open(url, '_blank');
    }
  }

  // ========================================
  // UTILITY METHODS
  // ========================================

  /**
   * Extracts the original filename from the blob name
   * Handles blob storage naming conventions to get user-friendly filenames
   * 
   * @param blobName - The blob storage name
   * @returns The extracted filename or original blob name
   */
  private extractFilename(blobName: string): string {
    const parts = blobName.split('/');
    return parts[parts.length - 1] || blobName;
  }

  // ========================================
  // TEMPLATE UTILITY METHODS
  // ========================================

  /**
   * Combines regular fields and checkbox groups into a single array for seamless highlighting
   * Used by templates to create unified item lists for styling and interaction
   * 
   * @param section - The section to process
   * @param sectionIndex - The index of the section within its step
   * @returns Array of items with type and data for template rendering
   */
  getCombinedSectionItems(section: any, sectionIndex: number): any[] {
    const items: any[] = [];
    
    // Add regular fields first
    if (section.fields && section.fields.length > 0) {
      section.fields.forEach((field: any) => {
        items.push({
          type: 'field',
          data: field
        });
      });
    }
    
    // Add checkbox groups after regular fields
    if (section.checkboxGroups && section.checkboxGroups.length > 0) {
      section.checkboxGroups.forEach((checkboxGroup: any) => {
        items.push({
          type: 'checkboxGroup',
          data: checkboxGroup
        });
      });
    }
    
    return items;
  }

  /**
   * Gets special combined section items for enhanced template functionality
   * Similar to getCombinedSectionItems but with additional processing capabilities
   * 
   * @param section - The section to process
   * @param sectionIndex - The index of the section within its step
   * @returns Array of items with type and data for template rendering
   */
  getSpecialCombinedSectionItems(section: any, sectionIndex: number): any[] {
    const items: any[] = [];

    // Add regular fields first
    if (section.fields && section.fields.length > 0) {
      section.fields.forEach((field: any) => {
        items.push({
          type: 'field',
          data: field
        });
      });
    }
    
    // Add checkbox groups after regular fields
    if (section.checkboxGroups && section.checkboxGroups.length > 0) {
      section.checkboxGroups.forEach((checkboxGroup: any) => {
        items.push({
          type: 'checkboxGroup',
          data: checkboxGroup
        });
      });
    }
    
    return items;
  }

  /**
   * Calculates continuous index for items within a step
   * Used for global highlighting and styling across step boundaries
   * 
   * @param step - The step containing the item
   * @param sectionIndex - The section index within the step
   * @param itemIndex - The item index within the section
   * @returns Continuous index across all items in the step
   */
  getContinuousItemIndex(step: any, sectionIndex: number, itemIndex: number): number {
    let continuousIndex = 0;
    
    // Add all items from previous sections in this step
    for (let s = 0; s < sectionIndex; s++) {
      const section = step.sections[s];
      const sectionItems = this.getCombinedSectionItems(section, s);
      continuousIndex += sectionItems.length;
    }
    
    // Add the current item index
    continuousIndex += itemIndex;
    
    return continuousIndex;
  }
}